import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { createNinaMemoryStore } from "../nina/memory/NinaMemoryStore";
import { readAcademyProgress } from "../nina/memory/NinaMemoryAcademyAdapter";
import { useOptionalUserStorage } from "../storage/UserStorageContext";

const NinaMemoryContext = createContext(null);

const createTemporaryStorage = () => {
  const values = new Map();

  return {
    get length() {
      return values.size;
    },
    key: (index) => Array.from(values.keys())[index] ?? null,
    getItem: (key) => {
      const normalizedKey = String(key);
      return values.has(normalizedKey) ? values.get(normalizedKey) : null;
    },
    setItem: (key, value) => {
      values.set(String(key), String(value));
    },
    removeItem: (key) => {
      values.delete(String(key));
    },
    clear: () => {
      values.clear();
    },
  };
};

const safeErrorMessage = (error, fallback = "AI memory could not be updated.") => {
  if (typeof error === "string") return error;
  if (typeof error?.message === "string") return error.message;
  return fallback;
};

export function NinaMemoryProvider({
  children,
  store = null,
  academyReader = null,
}) {
  const userStorage = useOptionalUserStorage();
  const [revision, setRevision] = useState(0);
  const [error, setError] = useState(null);

  const guestStorage = useMemo(
    () => (userStorage ? null : createTemporaryStorage()),
    [userStorage],
  );

  const resolvedStore = useMemo(
    () => store || createNinaMemoryStore(userStorage || guestStorage),
    [store, userStorage, guestStorage],
  );

  const resolvedAcademyReader = useMemo(
    () => academyReader || (() => readAcademyProgress(userStorage || guestStorage)),
    [academyReader, userStorage, guestStorage],
  );

  const refresh = useCallback(() => setRevision((current) => current + 1), []);

  useEffect(() => {
    const handleStorage = () => refresh();
    window.addEventListener("storage", handleStorage);
    window.addEventListener("anina:academy-progress-updated", refresh);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("anina:academy-progress-updated", refresh);
    };
  }, [refresh]);

  const runMutation = useCallback(
    (operation) => {
      try {
        const result = operation();
        setError(result.ok ? null : safeErrorMessage(result.error));
        if (result.ok) refresh();
        return result;
      } catch (operationError) {
        const message = safeErrorMessage(operationError);
        setError(message);
        return {
          ok: false,
          error: { code: "MEMORY_OPERATION_FAILED", message },
        };
      }
    },
    [refresh]
  );

  const value = useMemo(() => {
    void revision;
    const stored = resolvedStore.listRecords({ status: "all" });
    const storedRecords = stored.ok ? stored.value : [];
    const academy = resolvedAcademyReader();
    const academyRecords = academy.ok ? academy.value : [];
    const records = [...storedRecords, ...academyRecords];
    const summary = {
      total: records.length,
      active: records.filter((record) => record.status === "active").length,
      archived: records.filter((record) => record.status === "archived").length,
      academyProgress: records.filter((record) => record.category === "academy-progress").length,
      simulatedTrades: records.filter((record) => record.category === "simulated-trade").length,
      lessonsLearned: records.filter((record) => record.category === "lesson-learned").length,
      approvedRules: records.filter((record) => record.category === "user-approved-rule").length,
    };

    return {
      records,
      summary,
      error:
        error ||
        (stored.ok ? null : safeErrorMessage(stored.error)) ||
        (academy.ok ? null : safeErrorMessage(academy.error)),
      createRecord: (record) => runMutation(() => resolvedStore.createRecord(record)),
      updateRecord: (id, changes) => runMutation(() => resolvedStore.updateRecord(id, changes)),
      archiveRecord: (id) => runMutation(() => resolvedStore.archiveRecord(id)),
      restoreRecord: (id) => runMutation(() => resolvedStore.restoreRecord(id)),
      clearRecords: (options) => runMutation(() => resolvedStore.clearRecords(options)),
      exportRecords: () => resolvedStore.exportRecords(),
      refresh,
    };
  }, [resolvedStore, resolvedAcademyReader, revision, error, runMutation, refresh]);

  return (
    <NinaMemoryContext.Provider value={value}>
      {children}
    </NinaMemoryContext.Provider>
  );
}

export function useNinaMemory() {
  const context = useContext(NinaMemoryContext);
  if (!context) {
    throw new Error("useNinaMemory must be used inside NinaMemoryProvider.");
  }
  return context;
}

export default NinaMemoryContext;
